// frontend/src/hooks/useWorkflowDraft.js
import { useState, useEffect } from 'react';
import { consumeHandoff, writeDraft, readDraft, clearDraft } from '../lib/workflowState';

/**
 * useWorkflowDraft — connects a form to the cross-tab state mechanism.
 *
 * module  string  e.g. 'factory'
 * form    string  e.g. 'create_wo'
 *
 * Returns:
 *   prefill      object|null  Data to auto-fill immediately (UUID mode). Null if no UUID in URL.
 *   hasDraft     boolean      True if a named draft exists (no UUID in URL).
 *   restoreDraft () => void   Load draft into form — call from banner Restore button.
 *   discardDraft () => void   Delete draft — call from banner Discard button.
 *   clearDraft   () => void   Delete draft — call on form submit success or cancel.
 *
 * Usage:
 *   const { prefill, hasDraft, restoreDraft, discardDraft, clearDraft } =
 *     useWorkflowDraft('factory', 'create_wo');
 *
 *   // In form useEffect:
 *   useEffect(() => { if (prefill) setForm(merge(form, prefill)); }, [prefill]);
 *
 * UUID mode:  action_key in URL → consume handoff → write draft → set prefill (silent, no banner)
 * Draft mode: no action_key + named draft exists → set hasDraft = true (show restore banner)
 */
export function useWorkflowDraft(module, form) {
  const [prefill, setPrefill] = useState(null);
  const [hasDraft, setHasDraft] = useState(false);
  const [draftData, setDraftData] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const actionKey = params.get('action_key');

    if (actionKey) {
      // UUID mode — consume the handoff, write draft, auto-fill
      const payload = consumeHandoff(actionKey);
      if (payload) {
        writeDraft(module, form, payload);
        setPrefill(payload);
      }
      // Clean the URL param without triggering a navigation
      const url = new URL(window.location.href);
      url.searchParams.delete('action_key');
      window.history.replaceState({}, '', url.toString());
    } else {
      // No UUID — check for existing named draft
      const existing = readDraft(module, form);
      if (existing) {
        setDraftData(existing);
        setHasDraft(true);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const restoreDraft = () => {
    setPrefill(draftData);
    setHasDraft(false);
  };

  const discardDraft = () => {
    clearDraft(module, form);
    setHasDraft(false);
    setDraftData(null);
  };

  const clear = () => {
    clearDraft(module, form);
    setHasDraft(false);
    setPrefill(null);
    setDraftData(null);
  };

  return { prefill, hasDraft, restoreDraft, discardDraft, clearDraft: clear };
}
